import React, { useContext, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { VariantContextData } from "../variants/VariantBuilderContext";
import VariantSettings from "../variants/VariantSettings";
import StepSettings from "../steps/StepSettings";
import SectionSettings from "../sections/SectionSettings";
import SectionColumnSettings from "../section_columns/SectionColumnSettings";
import ElementSettings from "../elements/ElementSettings";
import StepsManager from "../steps/StepsManager";
import VariantSettingsTheme from "../variants/VariantSettingsTheme";
import VariantBuilderPanelLayers from "../variants/VariantBuilderPanelLayers";
import VariantBuilderPanelNew from "../variants/VariantBuilderPanelNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import QuizQuestionSettings from "../fields/QuizQuestionSettings";
import VariantBuilderCanvasRoundedCorner from "../variants/VariantBuilderCanvasRoundedCorner";
import AIChatPanel from "../variants/ai/AIChatPanel";
import RemixSparkleIcon from "../variants/ai/RemixSparkleIcon";
import { setAiChatFocus, useAiChatUnread } from "../variants/ai/useAiAssistant";
import useCodeIndicators from "../../hooks/useCodeIndicators";

const VariantBuilderPanel = (props) => {
  const [contextData, builder, setContextData] = useContext(VariantContextData);
  const panelToggle = useRef(null);
  const funnelButton = useRef(null);
  const createButton = useRef(null);
  const themeButton = useRef(null);
  const settingsButton = useRef(null);
  const layersButton = useRef(null);
  const aiButton = useRef(null);

  let panelClosed = contextData.panel_closed;
  const view = contextData.view;
  let cta = contextData.objects.cta;
  let variant = props.variant;
  let selected = builder.selected();
  const selectedObjectIsPresent =
    selected.object_id &&
    selected.object &&
    selected.object.toBeDeleted !== true;
  const selectedObjectHasLayerSettings =
    selectedObjectIsPresent &&
    ["sections", "section_columns", "elements", "fields"].includes(
      selected.object_type
    );
  let selected_label;
  let addElement;

  if (selected.object_type) {
    if (selected.object_type !== "variants") {
      selected_label = capitalizeFirstLetter(selected.object_type).slice(0, -1);
    }

    if (
      selected.object_type == "sections" &&
      selected.object &&
      selected.object.element_id
    ) {
      selected_label = "Row";
    }

    if (selected.object_type == "section_columns") {
      selected_label = "Column";
    }

    if (selected.object_type == "elements" && selected.object) {
      selected_label = capitalizeFirstLetter(selected.object.element_type);
    }
  }

  const availableStepIds = Object.values(contextData.objects.variant.steps)
    .filter((step) => step.toBeDeleted !== true)
    .map((step) => step.id);

  const availableSectionIds = Object.values(
    contextData.objects.variant.sections
  )
    .filter(
      (section) =>
        section.toBeDeleted !== true &&
        availableStepIds.includes(section.step_id)
    )
    .map((section) => section.id);

  const elementWarnings = ElementWarningsService.getWarningCount(variant);

  const aiUnread = useAiChatUnread(variant.id);

  const codeIndicators = useCodeIndicators();
  const allIndicators = codeIndicators.all();
  const variantIndicator = allIndicators.variant[0];

  const tabBadge = (indicator) =>
    indicator && (
      <div className="w-[11px] h-[11px] leading-[11px] font-semibold rounded-full text-xs text-white text-center">
        <FontAwesomeIcon
          icon={indicator.icon}
          style={{ color: indicator.color }}
        />
      </div>
    );

  useEffect(() => {
    if (props.page == "builder") {
      let panel = document.getElementById("options-panel");
      let scrollable = document.querySelector(".options-scrollable");

      selected = builder.selected();
      scrollable.scrollTop = 0;
    }
  }, [selected.object_id, selected.object_collapse, contextData.new_object]);

  const handleSelect = (e) => {
    builder.view(e.detail.view);

    if (e.detail.callback) {
      e.detail.callback();
    }
  };

  useEffect(() => {
    document.addEventListener("navigateTab", handleSelect);

    return function cleanup() {
      document.removeEventListener("navigateTab", handleSelect);
    };
  });

  return (
    <React.Fragment>
      {props.page == "builder" && (
        <React.Fragment>
          <div
            id="options-panel"
            data-canvas-boundary="left"
            cta-type={cta.cta_type}
            className={`options-panel ${
              panelClosed ? "options-panel-closed" : "options-panel-open"
            } fixed z-10 bg-white dark:bg-slate-950 rounded-xl shadow`}
            data-position="unscrolled"
            onClick={(e) => {
              if (panelClosed) {
                builder.panel(false);
              }
            }}
            style={{
              height: "calc(100vh - 80px)",
              display: view ? "block" : "none",
            }}
          >
            <div
              ref={panelToggle}
              onClick={(e) => {
                builder.view(null);
              }}
              className={
                "absolute top-9 right-5 text-black dark:text-white cursor-pointer z-30"
              }
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.2996 4.09998L4.09961 12.3M12.2996 12.3L4.09961 4.09998"
                  stroke={contextData.dark_mode ? "#fff" : "black"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div
              className={`row hard-center options-scrollable px-[17px] ${
                view == "ai" ? "!overflow-hidden" : ""
              }`}
            >
              <div
                className={`row hard-center options-container ${
                  view == "ai" ? "h-full" : ""
                }`}
              >
                {view == "create" && (
                  <VariantBuilderPanelNew
                    step_templates={props.step_templates}
                    section_templates={props.section_templates}
                    element_templates={props.element_templates}
                    field_templates={props.field_templates}
                    current_user={props.current_user}
                    FILESTACK_API_KEY={props.FILESTACK_API_KEY}
                  />
                )}
                {view == "theme" && <VariantSettingsTheme {...props} />}
                {view == "funnel" && !selectedObjectIsPresent && (
                  <StepsManager step_templates={props.step_templates} />
                )}
                {view == "funnel" && selectedObjectIsPresent && (
                  <StepSettings />
                )}
                {view == "layers" && !selectedObjectHasLayerSettings && (
                  <VariantBuilderPanelLayers {...props} />
                )}
                {view == "layers" && selectedObjectHasLayerSettings && (
                  <React.Fragment>
                    {selected.object_type == "sections" && <SectionSettings />}
                    {selected.object_type == "section_columns" && (
                      <SectionColumnSettings
                        section_column={selected.object}
                        settings_only={true}
                      />
                    )}
                    {selected.object_type == "elements" && (
                      <ElementSettings
                        field_templates={props.field_templates}
                      />
                    )}
                    {selected.object_type == "fields" && (
                      <QuizQuestionSettings />
                    )}
                  </React.Fragment>
                )}
                {view == "settings" &&
                  selected.object_type == "variants" &&
                  selected.object_id && <VariantSettings {...props} />}
                {/* stays mounted so replies land (and badge) while on other tabs;
                    usage is feature-gated inside AIChatPanel */}
                <div
                  className="w-full h-full flex flex-col min-h-0"
                  style={{
                    display: view == "ai" ? "flex" : "none",
                  }}
                >
                  <AIChatPanel {...props} />
                </div>
              </div>
            </div>
          </div>

          <div
            data-canvas-boundary="left"
            className="h-screen fixed bg-white dark:bg-slate-950 mt-[49px] pt-4 center z-10"
            style={{
              width: "65px",
            }}
          >
            <VariantBuilderCanvasRoundedCorner
              vertical="top"
              horizontal="left"
            />

            <div className="px-2 mt-2">
              <button
                ref={funnelButton}
                className={`relative border-2 border-solid pt-1 pb-2 px-1 center rounded-lg text-lg cursor-pointer ${
                  view == "funnel"
                    ? "bg-blue-100 dark:bg-slate-800 hover:bg-blue-100 dark:hover:bg-slate-800 text-blue-600 dark:text-white hover:text-blue-600 dark:hover:text-white border-blue-100 dark:border-slate-800 hover:border-blue-100 dark:hover:border-slate-800"
                    : "bg-white dark:bg-slate-950 hover:bg-blue-100 dark:hover:bg-slate-800 text-black dark:text-gray-300 hover:text-blue-600 dark:hover:text-white border-white dark:border-slate-950 hover:border-blue-100 dark:hover:border-slate-800"
                }`}
                onClick={() => {
                  builder.view("funnel");
                }}
                onMouseOver={() => {
                  const svgPath =
                    funnelButton.current.querySelector("svg path");
                  if (view !== "funnel" && svgPath && !contextData.dark_mode) {
                    svgPath.setAttribute("stroke", "#003FFF");
                  }
                }}
                onMouseLeave={() => {
                  const svgPath =
                    funnelButton.current.querySelector("svg path");
                  if (view !== "funnel" && svgPath && !contextData.dark_mode) {
                    svgPath.setAttribute("stroke", "black");
                  }
                }}
                style={{
                  width: "55px",
                }}
                data-tab="steps"
              >
                <svg
                  className="-mb-1"
                  width="20"
                  height="20"
                  viewBox="0 0 20 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.38429 10.0002H14.6151M3.33301 5.8335H16.6663M8.46121 14.1668H11.5381"
                    stroke={
                      view == "funnel"
                        ? contextData.dark_mode
                          ? "white"
                          : `#003FFF`
                        : contextData.dark_mode
                        ? "#fff"
                        : "black"
                    }
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Funnel
                <div
                  className="absolute -top-[4px] -right-[4px] flex gap-1"
                  style={{ flexDirection: "column" }}
                >
                  {elementWarnings > 0 && (
                    <div className="w-[11px] h-[11px] leading-[11px] font-semibold rounded-full text-xs bg-red-700 text-white text-center">
                      {elementWarnings}
                    </div>
                  )}
                </div>
              </button>
            </div>

            <div className="px-2 mt-2">
              <button
                ref={themeButton}
                className={`border-2 border-solid pt-3 pb-2 px-2 rounded-lg text-lg cursor-pointer ${
                  view == "theme"
                    ? "bg-blue-100 dark:bg-slate-800 hover:bg-blue-100 dark:hover:bg-slate-800 text-blue-600 dark:text-white hover:text-blue-600 dark:hover:text-white border-blue-100 dark:border-slate-800 hover:border-blue-100 dark:hover:border-slate-800"
                    : "bg-white dark:bg-slate-950 hover:bg-blue-100 dark:hover:bg-slate-800 text-black dark:text-gray-300 hover:text-blue-600 dark:hover:text-white border-white dark:border-slate-950 hover:border-blue-100 dark:hover:border-slate-800"
                }`}
                onClick={() => {
                  builder.view("theme");
                }}
                style={{
                  width: "55px",
                }}
                data-tab="theme"
                onMouseOver={() => {
                  const svgPath = themeButton.current.querySelector("svg path");
                  if (view !== "theme" && svgPath && !contextData.dark_mode) {
                    svgPath.setAttribute("stroke", "#003FFF");
                  }
                }}
                onMouseLeave={() => {
                  const svgPath = themeButton.current.querySelector("svg path");
                  if (view !== "theme" && svgPath && !contextData.dark_mode) {
                    svgPath.setAttribute("stroke", "black");
                  }
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.6996 10.8C11.2978 12.0573 10.2878 13.0626 8.99961 13.5M8.99965 16.2C6.02211 16.2 3.59961 13.9184 3.59961 11.114C3.59961 7.2 8.99969 1.8 8.99969 1.8C8.99969 1.8 14.3996 7.2 14.3996 11.114C14.3996 13.9185 11.9772 16.2 8.99965 16.2Z"
                    stroke={
                      view == "theme"
                        ? contextData.dark_mode
                          ? "white"
                          : `#003FFF`
                        : contextData.dark_mode
                        ? "#fff"
                        : "black"
                    }
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Theme
              </button>
            </div>

            <div className="px-2 mt-2">
              <button
                ref={layersButton}
                className={`relative border-2 border-solid pt-3 pb-2 px-1 center rounded-lg text-lg cursor-pointer ${
                  view == "layers"
                    ? "bg-blue-100 dark:bg-slate-800 hover:bg-blue-100 dark:hover:bg-slate-800 text-blue-600 dark:text-white hover:text-blue-600 dark:hover:text-white border-blue-100 dark:border-slate-800 hover:border-blue-100 dark:hover:border-slate-800"
                    : "bg-white dark:bg-slate-950 hover:bg-blue-100 dark:hover:bg-slate-800 text-black dark:text-gray-300 hover:text-blue-600 dark:hover:text-white border-white dark:border-slate-950 hover:border-blue-100 dark:hover:border-slate-800"
                }`}
                onClick={() => {
                  builder.select("variants", variant.id);
                  builder.view("layers");

                  setTimeout(() => {
                    const layerItem = document.querySelector(
                      `.layers-item-container[data-object-type="${selected.object_type}"][data-object-id="${selected.object_id}"]`
                    );
                    if (layerItem) {
                      layerItem.scrollIntoView({
                        behavior: "instant",
                        block: "center",
                        inline: "center",
                      });
                    }
                  }, 500);
                }}
                style={{
                  width: "55px",
                }}
                data-tab="content"
                onMouseOver={() => {
                  const svgPath =
                    layersButton.current.querySelector("svg path");
                  if (view !== "layers" && svgPath && !contextData.dark_mode) {
                    svgPath.setAttribute("stroke", "#003FFF");
                  }
                }}
                onMouseLeave={() => {
                  const svgPath =
                    layersButton.current.querySelector("svg path");
                  if (view !== "layers" && svgPath && !contextData.dark_mode) {
                    svgPath.setAttribute("stroke", "black");
                  }
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.2008 8.98134L9.00078 12.6719L1.80078 8.98134M16.2008 12.5094L9.00078 16.2L1.80078 12.5094M9.00078 1.79999L16.2008 5.49055L9.00078 9.18111L1.80078 5.49055L9.00078 1.79999Z"
                    stroke={
                      view == "layers"
                        ? contextData.dark_mode
                          ? "white"
                          : `#003FFF`
                        : contextData.dark_mode
                        ? "#fff"
                        : "black"
                    }
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Content
              </button>
            </div>

            <div className="px-2 mt-2">
              <button
                ref={settingsButton}
                className={`relative border-2 border-solid pt-3 pb-2 px-1 center rounded-lg text-lg cursor-pointer ${
                  view == "settings"
                    ? "bg-blue-100 dark:bg-slate-800 hover:bg-blue-100 dark:hover:bg-slate-800 text-blue-600 dark:text-white hover:text-blue-600 dark:hover:text-white border-blue-100 dark:border-slate-800 hover:border-blue-100 dark:hover:border-slate-800"
                    : "bg-white dark:bg-slate-950 hover:bg-blue-100 dark:hover:bg-slate-800 text-black dark:text-gray-300 hover:text-blue-600 dark:hover:text-white border-white dark:border-slate-950 hover:border-blue-100 dark:hover:border-slate-800"
                }`}
                onClick={() => {
                  dispatchCustomEvent("selectObject", {
                    object_type: "variants",
                    object_id: variant.id,
                    panel_closed: false,
                  });
                }}
                style={{
                  width: "55px",
                }}
                data-tab="cta"
                onMouseOver={() => {
                  const svgPath =
                    settingsButton.current.querySelector("svg path");
                  if (
                    view !== "settings" &&
                    svgPath &&
                    !contextData.dark_mode
                  ) {
                    svgPath.setAttribute("fill", "#003FFF");
                  }
                }}
                onMouseLeave={() => {
                  const svgPath =
                    settingsButton.current.querySelector("svg path");
                  if (
                    view !== "settings" &&
                    svgPath &&
                    !contextData.dark_mode
                  ) {
                    svgPath.setAttribute("fill", "black");
                  }
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11.5515 2.88543C11.1556 1.2604 8.84306 1.2604 8.44722 2.88543C8.38812 3.12961 8.2722 3.3564 8.1089 3.54732C7.94559 3.73825 7.73952 3.88792 7.50744 3.98416C7.27537 4.0804 7.02385 4.12049 6.77335 4.10116C6.52285 4.08184 6.28046 4.00364 6.06589 3.87294C4.63668 3.00209 3.00121 4.63754 3.87207 6.06672C4.43459 6.98966 3.93562 8.19384 2.88558 8.44905C1.25949 8.84385 1.25949 11.1574 2.88558 11.5512C3.12984 11.6104 3.35667 11.7264 3.5476 11.8898C3.73852 12.0532 3.88816 12.2594 3.98431 12.4916C4.08046 12.7238 4.12041 12.9755 4.1009 13.226C4.0814 13.4766 4.003 13.719 3.87207 13.9335C3.00121 15.3627 4.63668 16.9981 6.06589 16.1273C6.28042 15.9964 6.52283 15.918 6.7734 15.8985C7.02396 15.879 7.27559 15.9189 7.50779 16.0151C7.73999 16.1112 7.9462 16.2608 8.10963 16.4518C8.27306 16.6427 8.38908 16.8695 8.44826 17.1138C8.84306 18.7398 11.1567 18.7398 11.5504 17.1138C11.6098 16.8696 11.726 16.643 11.8894 16.4522C12.0529 16.2614 12.2591 16.1119 12.4912 16.0157C12.7233 15.9196 12.9748 15.8796 13.2253 15.899C13.4758 15.9184 13.7182 15.9966 13.9328 16.1273C15.362 16.9981 16.9975 15.3627 16.1266 13.9335C15.9959 13.7189 15.9177 13.4766 15.8983 13.2261C15.8789 12.9756 15.9189 12.724 16.0151 12.4919C16.1112 12.2598 16.2607 12.0536 16.4515 11.8902C16.6423 11.7267 16.869 11.6106 17.1131 11.5512C18.7392 11.1564 18.7392 8.84281 17.1131 8.44905C16.8689 8.38988 16.642 8.27386 16.4511 8.11043C16.2602 7.94701 16.1105 7.7408 16.0144 7.5086C15.9182 7.2764 15.8783 7.02478 15.8978 6.77422C15.9173 6.52366 15.9957 6.28125 16.1266 6.06672C16.9975 4.63754 15.362 3.00209 13.9328 3.87294C13.7183 4.00386 13.4759 4.08226 13.2253 4.10177C12.9747 4.12127 12.7231 4.08132 12.4909 3.98517C12.2587 3.88902 12.0525 3.73939 11.8891 3.54847C11.7256 3.35754 11.6096 3.13072 11.5504 2.88647L11.5515 2.88543ZM9.99935 13.1252C10.8282 13.1252 11.6231 12.7959 12.2091 12.2099C12.7952 11.6238 13.1244 10.8289 13.1244 10.0001C13.1244 9.1713 12.7952 8.37644 12.2091 7.79038C11.6231 7.20432 10.8282 6.87507 9.99935 6.87507C9.17052 6.87507 8.37564 7.20432 7.78957 7.79038C7.2035 8.37644 6.87425 9.1713 6.87425 10.0001C6.87425 10.8289 7.2035 11.6238 7.78957 12.2099C8.37564 12.7959 9.17052 13.1252 9.99935 13.1252Z"
                    fill={
                      view == "settings"
                        ? contextData.dark_mode
                          ? "white"
                          : `#003FFF`
                        : contextData.dark_mode
                        ? "#fff"
                        : "black"
                    }
                  />
                </svg>
                Settings
                <div
                  className="absolute -top-[4px] -right-[4px] flex gap-1"
                  style={{ flexDirection: "column" }}
                >
                  {tabBadge(variantIndicator)}
                </div>
              </button>
            </div>

            <div className="px-2 mt-2">
              <button
                ref={createButton}
                className={`border-2 border-solid pt-3 pb-2 px-1 center rounded-lg text-lg cursor-pointer ${
                  view == "create"
                    ? "bg-blue-100 dark:bg-slate-800 hover:bg-blue-100 dark:hover:bg-slate-800 text-blue-600 dark:text-white hover:text-blue-600 dark:hover:text-white border-blue-100 dark:border-slate-800 hover:border-blue-100 dark:hover:border-slate-800"
                    : "bg-white dark:bg-slate-950 hover:bg-blue-100 dark:hover:bg-slate-800 text-black dark:text-gray-300 hover:text-blue-600 dark:hover:text-white border-white dark:border-slate-950 hover:border-blue-100 dark:hover:border-slate-800"
                }`}
                onClick={() => {
                  dispatchCustomEvent("newObject", {
                    object_type: "Elements",
                    element_type: "all",
                    panel_closed: false,
                  });
                }}
                style={{
                  width: "55px",
                }}
                title="Create"
                onMouseOver={() => {
                  const svgPath =
                    createButton.current.querySelector("svg path");
                  if (view !== "create" && svgPath && !contextData.dark_mode) {
                    svgPath.setAttribute("stroke", "#003FFF");
                  }
                }}
                onMouseLeave={() => {
                  const svgPath =
                    createButton.current.querySelector("svg path");
                  if (view !== "create" && svgPath && !contextData.dark_mode) {
                    svgPath.setAttribute("stroke", "black");
                  }
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.5315 8.99959H9.00028M9.00028 8.99959H6.46903M9.00028 8.99959V11.5308M9.00028 8.99959L9.00028 6.46835M15.75 4.78124L15.75 13.2188C15.75 14.6167 14.6167 15.75 13.2187 15.75H4.78125C3.38328 15.75 2.25 14.6167 2.25 13.2188V4.78124C2.25 3.38327 3.38328 2.25 4.78125 2.25H13.2188C14.6167 2.25 15.75 3.38327 15.75 4.78124Z"
                    stroke={
                      view == "create"
                        ? contextData.dark_mode
                          ? "white"
                          : `#003FFF`
                        : contextData.dark_mode
                        ? "#fff"
                        : "black"
                    }
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                Create
              </button>
            </div>

            <div className="px-2 mt-2">
              <button
                ref={aiButton}
                className={`relative border-2 border-solid pt-3 pb-2 px-1 center rounded-lg text-lg cursor-pointer ${
                  view == "ai"
                    ? "bg-blue-100 dark:bg-slate-800 hover:bg-blue-100 dark:hover:bg-slate-800 text-blue-600 dark:text-white hover:text-blue-600 dark:hover:text-white border-blue-100 dark:border-slate-800 hover:border-blue-100 dark:hover:border-slate-800"
                    : "bg-white dark:bg-slate-950 hover:bg-blue-100 dark:hover:bg-slate-800 text-black dark:text-gray-300 hover:text-blue-600 dark:hover:text-white border-white dark:border-slate-950 hover:border-blue-100 dark:hover:border-slate-800"
                }`}
                onClick={() => {
                  // capture the selection before builder.view clears it, so
                  // the chat opens focused on the selected object
                  setAiChatFocus(
                    variant.id,
                    contextData.selected_object_type,
                    contextData.selected_object_id
                  );
                  builder.view("ai");
                }}
                style={{
                  width: "55px",
                }}
                data-tab="ai"
                title="AI Chat"
              >
                <div className="flex justify-center mb-1">
                  <RemixSparkleIcon width={18} height={19} />
                </div>
                <span className="whitespace-nowrap">AI Chat</span>
                {aiUnread > 0 && (
                  <div className="absolute -top-[4px] -right-[4px] w-[11px] h-[11px] leading-[11px] font-semibold rounded-full text-xs bg-red-700 text-white text-center">
                    {aiUnread}
                  </div>
                )}
              </button>
            </div>

            <div className="px-2 mt-2 absolute bottom-[65px]">
              <button
                className={`border-solid bg-transparent pt-3 pb-2 px-1 center rounded-lg text-lg cursor-pointer border-0 ${
                  contextData.zoomable_canvas_locked
                    ? "hover:bg-blue-100 dark:hover:bg-slate-800 text-blue-600 dark:text-white hover:text-blue-600 dark:hover:text-white"
                    : "hover:bg-blue-100 dark:hover:bg-slate-800 text-black dark:text-gray-300 hover:text-blue-600 dark:hover:text-white"
                }`}
                onClick={() => {
                  builder.toggleZoomableCanvas();
                }}
                style={{
                  width: "55px",
                }}
                data-tab="content"
                title="Locked/Unlocked Canvas"
              >
                <FontAwesomeIcon
                  icon="fas fa-display"
                  className="text-2xl mb-0 mx-3"
                />
                {contextData.zoomable_canvas_locked ? "Locked" : "Unlocked"}
              </button>

              <button
                className={`border-solid bg-transparent pt-3 pb-2 px-1 center rounded-lg text-lg cursor-pointer border-0 ${
                  contextData.dark_mode
                    ? "hover:bg-blue-100 dark:hover:bg-slate-800 text-blue-600 dark:text-white hover:text-blue-600 dark:hover:text-white"
                    : "hover:bg-blue-100 dark:hover:bg-slate-800 text-black dark:text-gray-300 hover:text-blue-600 dark:hover:text-white"
                }`}
                onClick={() => {
                  builder.toggleDarkMode();
                }}
                style={{
                  width: "55px",
                }}
                data-tab="content"
              >
                <FontAwesomeIcon
                  icon="fas fa-adjust"
                  className="text-2xl mb-0 mx-3"
                />
                {!contextData.dark_mode ? "Light" : "Dark"}
              </button>
            </div>

            <VariantBuilderCanvasRoundedCorner
              vertical="bottom"
              horizontal="left"
            />
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default VariantBuilderPanel;
